/**
 * The six flags the language section wears.
 *
 * Drawn rather than typed: the flag emoji this section used to lean on is a
 * regional-indicator pair that Windows renders as two grey letters, so half
 * the people this page is written for would have seen "TR" where a flag was
 * meant to be. An SVG looks the same everywhere and stays crisp when the tile
 * is scaled up on a wide screen.
 *
 * Each flag is drawn into a 24x16 box so the whole set shares one aspect
 * ratio and the grid stays even.
 */

export type FlagCode = 'tr' | 'en' | 'de' | 'fr' | 'es' | 'it';

/** Language name in its own language, how a picker is read fastest. */
export const FLAG_LABELS: Record<FlagCode, string> = {
  tr: 'Türkçe',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
};

export const FLAG_CODES: FlagCode[] = ['tr', 'en', 'de', 'fr', 'es', 'it'];

function Flag({ code }: { code: FlagCode }) {
  switch (code) {
    // To the flag law's proportions, with the height as G: the crescent's outer
    // circle is 0.5G across and centred 0.5G from the hoist, the inner one 0.4G
    // across and 0.0625G further out, and the star sits in a circle 0.25G
    // across, a third of G beyond the inner circle, one point toward the hoist.
    // The star is computed from that, not drawn by eye: the path here before
    // was not a five-pointed star at all.
    case 'tr':
      return (
        <>
          <rect width="24" height="16" fill="#E30A17" />
          <circle cx="8" cy="8" r="4" fill="#fff" />
          <circle cx="9" cy="8" r="3.2" fill="#E30A17" />
          <path
            fill="#fff"
            d="M11.13 8.00L12.52 7.55L12.52 6.10L13.37 7.27L14.75 6.82L13.90 8.00L14.75 9.18L13.37 8.73L12.52 9.90L12.52 8.45Z"
          />
        </>
      );
    // The Union Jack, drawn in the order it is layered: white saltire, red
    // saltire, white cross, red cross.
    case 'en':
      return (
        <>
          <rect width="24" height="16" fill="#012169" />
          <path d="M0 0 24 16M24 0 0 16" stroke="#fff" strokeWidth="3.2" />
          <path
            d="M0 0 24 16M24 0 0 16"
            stroke="#C8102E"
            strokeWidth="1.9"
            clipPath="url(#krevya-uj)"
          />
          <clipPath id="krevya-uj">
            <path d="M12 8h12v8ZM12 8V0h12ZM12 8H0V0ZM12 8v8H0Z" />
          </clipPath>
          <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5.3" />
          <path d="M12 0v16M0 8h24" stroke="#C8102E" strokeWidth="3.2" />
        </>
      );
    case 'de':
      return (
        <>
          <rect width="24" height="16" fill="#000" />
          <rect y="5.33" width="24" height="5.34" fill="#DD0000" />
          <rect y="10.67" width="24" height="5.33" fill="#FFCE00" />
        </>
      );
    case 'fr':
      return (
        <>
          <rect width="24" height="16" fill="#fff" />
          <rect width="8" height="16" fill="#002395" />
          <rect x="16" width="8" height="16" fill="#ED2939" />
        </>
      );
    case 'es':
      return (
        <>
          <rect width="24" height="16" fill="#AA151B" />
          <rect y="4" width="24" height="8" fill="#F1BF00" />
        </>
      );
    case 'it':
      return (
        <>
          <rect width="24" height="16" fill="#fff" />
          <rect width="8" height="16" fill="#008C45" />
          <rect x="16" width="8" height="16" fill="#CD212A" />
        </>
      );
  }
}

export function LandingFlag({ code, title }: { code: FlagCode; title?: string }) {
  return (
    <svg
      className="lp-flag-art"
      viewBox="0 0 24 16"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : 'true'}
      aria-label={title}
      focusable="false"
    >
      <Flag code={code} />
    </svg>
  );
}
